import { useState, useEffect } from 'react';
// import type { Vendor } from '../../types'; // You might need to update this type definition based on API response

// Define Vendor interface locally or import it if it matches the API response structure
export interface Vendor {
  vendorIdx: number;
  name: string;
  status: string;
  vendorCategoryIdx: number;
  commissionRate: string | number | null;
  managerName: string;
  vendorType?: string; // 'local' etc.
  contextType?: string;
  relationshipType?: string;
  ownerVendorIdx?: number | null;
  hostVendorIdx?: number | null;
  isOwnerVendor?: boolean | string | number;
  isPartnerVendor?: boolean | string | number;
  isPartnershipVendor?: boolean | string | number;
  isGuestContext?: boolean | string | number;
  isLocal?: boolean; // true: 단방향 발주 대상 (미초대 local 업체)
  address: string;
  memo: string;
  email: string;
  contact: string;
  category?: {
    vendorCategoryIdx: number;
    ownerVendorIdx: number | null;
    name: string; // e.g., "스튜디오"
    // ... other category fields
  } | null;
}

interface PartnerVendorItem {
  vendorIdx: number;
  name: string;
  vendorType?: string;
  categoryName?: string;
  managerName?: string;
  contact?: string;
  email?: string;
  commissionRate?: string | number | null;
  status?: string;
  vendorCategoryIdx?: number;
  isLocal?: boolean;
}

type RequestError = Error & { status?: number };

const PAGE_SIZE = 20;

const buildHttpError = async (response: Response): Promise<RequestError> => {
  const errorPayload = await response.json().catch(() => null);
  const message = errorPayload?.message || errorPayload?.data?.message || `HTTP ${response.status}`;
  const error = new Error(message) as RequestError;
  error.status = response.status;
  return error;
};

const mapPartnerVendorToView = (item: PartnerVendorItem): Vendor | null => {
  const vendorIdx = Number(item.vendorIdx);
  if (!Number.isFinite(vendorIdx) || vendorIdx <= 0) return null;

  const vendorCategoryIdx = Number(item.vendorCategoryIdx);
  const normalizedCategoryIdx =
    Number.isFinite(vendorCategoryIdx) && vendorCategoryIdx > 0 ? vendorCategoryIdx : 0;

  return {
    vendorIdx,
    name: item.name || '',
    status: item.status || 'ACTIVE',
    vendorCategoryIdx: normalizedCategoryIdx,
    commissionRate: item.commissionRate ?? 0,
    managerName: item.managerName || '',
    vendorType: item.vendorType,
    isLocal: item.isLocal ?? false,
    address: '',
    memo: '',
    email: item.email || '',
    contact: item.contact || '',
    category: {
      vendorCategoryIdx: normalizedCategoryIdx,
      ownerVendorIdx: null,
      name: item.categoryName || '미지정',
    },
  };
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // vendors: Vendor[]; // Removed
  onSelect: (vendor: Vendor) => void;
  // searchTerm: string; // Removed (handled internally)
  // setSearchTerm: (term: string) => void; // Removed (handled internally)
}

export default function VendorSelectModal({ isOpen, onClose, onSelect }: Props) {
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchVendors(1, false);
    }
  }, [isOpen, searchTerm]); // Re-fetch when modal opens or search term changes

  const applyVendorList = (incoming: Vendor[], append: boolean) => {
    setVendorList((prev) => {
      if (!append) return incoming;
      const merged = [...prev, ...incoming];
      const deduped = new Map<number, Vendor>();
      merged.forEach((vendor) => {
        deduped.set(vendor.vendorIdx, vendor);
      });
      return Array.from(deduped.values());
    });
  };

  const fetchVendors = async (targetPage: number, append: boolean) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    if (!append) {
      setErrorMessage('');
    }
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('로그인 세션이 만료되었습니다. 다시 로그인해주세요.');

      const params = new URLSearchParams();
      params.append('page', String(Math.max(1, targetPage)));
      params.append('size', String(PAGE_SIZE));
      const trimmedQuery = searchTerm.trim();
      if (trimmedQuery) {
        params.append('q', trimmedQuery);
      }

      const response = await fetch(`https://api.wedmin.biz/order/partner-vendors?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw await buildHttpError(response);
      }

      const result = await response.json();
      const items: PartnerVendorItem[] = Array.isArray(result?.data?.items) ? result.data.items : [];
      const mappedVendors = items
        .map((item) => mapPartnerVendorToView(item))
        .filter((vendor): vendor is Vendor => Boolean(vendor))
      applyVendorList(mappedVendors, append);

      const responsePage = Number(result?.data?.page);
      const responseTotal = Number(result?.data?.total);
      const responseHasNext = result?.data?.hasNext;

      setPage(Number.isFinite(responsePage) && responsePage > 0 ? responsePage : targetPage);
      setTotal(Number.isFinite(responseTotal) && responseTotal >= 0 ? responseTotal : mappedVendors.length);
      setHasNext(typeof responseHasNext === 'boolean' ? responseHasNext : mappedVendors.length >= PAGE_SIZE);
      if (!append) {
        setErrorMessage('');
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      const message =
        error instanceof Error && error.message
          ? error.message
          : '업체 목록 조회에 실패했습니다. 잠시 후 다시 시도해주세요.';
      if (!append) {
        setPage(1);
        setTotal(0);
        setHasNext(false);
        setVendorList([]);
        setErrorMessage(message);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] flex flex-col"> {/* Changed to flex-col for layout */}
        
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">업체 선택</h2>
              <p className="text-sm text-gray-600 mt-1">발주할 업체를 선택하세요</p>
              <p className="text-xs text-amber-700 mt-1">미초대 업체는 단방향 발주로 진행됩니다.</p>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          
          {/* 검색 입력 */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
            <input
              type="text"
              placeholder="업체명, 카테고리, 담당자로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          {!loading && (
            <div className="mt-2 space-y-1">
              <p className="text-xs text-gray-500">총 {total}개 업체</p>
              {errorMessage && <p className="text-xs text-red-600">{errorMessage}</p>}
            </div>
          )}
        </div>

        {/* Content List */}
        <div className="p-6 overflow-y-auto flex-1"> {/* Added flex-1 and overflow-y-auto */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* 파트너 업체 (양방향) */}
              {vendorList.filter(v => !v.isLocal).length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-1 pb-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">파트너 업체</span>
                    <span className="text-xs text-gray-400">({vendorList.filter(v => !v.isLocal).length})</span>
                  </div>
                  {vendorList.filter(v => !v.isLocal).map((vendor) => (
                    <div
                      key={vendor.vendorIdx}
                      onClick={() => onSelect(vendor)}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                            <i className="ri-building-line text-blue-600 text-xl"></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{vendor.name}</h3>
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                                {vendor.category?.name || '미지정'}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.managerName}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.contact}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.email || '-'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600 mb-1">수수료율</div>
                          <div className="text-lg font-bold text-purple-600">{vendor.commissionRate || '0'}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* 등록 업체 - 미초대 (단방향) */}
              {vendorList.filter(v => v.isLocal).length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-3 pb-1 border-t border-gray-100 mt-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">등록 업체 (미초대)</span>
                    <span className="text-xs text-gray-400">({vendorList.filter(v => v.isLocal).length})</span>
                    <span className="text-xs text-amber-600">단방향 발주</span>
                  </div>
                  {vendorList.filter(v => v.isLocal).map((vendor) => (
                    <div
                      key={vendor.vendorIdx}
                      onClick={() => onSelect(vendor)}
                      className="border border-amber-200 rounded-lg p-4 hover:border-amber-400 hover:bg-amber-50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                            <i className="ri-building-2-line text-amber-600 text-xl"></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{vendor.name}</h3>
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                                {vendor.category?.name || '미지정'}
                              </span>
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">
                                미초대
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.managerName || '-'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.contact || '-'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                                <span>{vendor.email || '-'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600 mb-1">수수료율</div>
                          <div className="text-lg font-bold text-purple-600">{vendor.commissionRate || '0'}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {!loading && vendorList.length === 0 && (
                <div className="text-center py-12">
                  <i className="ri-building-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">선택 가능한 파트너 업체가 없습니다.</p>
                </div>
              )}
              {!loading && vendorList.length > 0 && hasNext && (
                <div className="pt-2 flex justify-center">
                  <button
                    type="button"
                    onClick={() => fetchVendors(page + 1, true)}
                    disabled={loadingMore}
                    className={`px-4 py-2 text-sm rounded-lg border ${
                      loadingMore
                        ? 'text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed'
                        : 'text-gray-700 border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    {loadingMore ? '불러오는 중...' : '더 보기'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end rounded-b-2xl">
          <button 
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}
